import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// The CRM connection is baked into the bundle at build time (Vite). A wrong or
// stale value is the classic cause of "everything looks logged in (cached
// session) but every CRM query hangs on 'Lade…' forever". So we validate the
// values up front and fail loud instead of constructing a client that points
// at a dead host. See README → Troubleshooting.
function detectConfigError(u: string | undefined, k: string | undefined): string | null {
  if (!u || !u.trim()) return 'VITE_SUPABASE_URL ist nicht gesetzt.';
  if (/YOUR-PROJECT|<project>|undefined/i.test(u)) {
    return 'VITE_SUPABASE_URL ist noch ein Platzhalter — auf das aktuelle CRM-Projekt setzen.';
  }
  if (!/^https:\/\/[a-z0-9-]+\.supabase\.co\/?$/i.test(u.trim())) {
    return `VITE_SUPABASE_URL sieht nicht nach einer Supabase-Projekt-URL aus: "${u}".`;
  }
  if (!k || !k.trim()) return 'VITE_SUPABASE_PUBLISHABLE_KEY ist nicht gesetzt.';
  if (/YOUR-|PUBLISHABLE-KEY|sb_publishable_\.\.\.|dein-publishable/i.test(k)) {
    return 'VITE_SUPABASE_PUBLISHABLE_KEY ist noch ein Platzhalter.';
  }
  return null;
}

export const supabaseConfigError = detectConfigError(url, key);

// Host portion of the configured URL, for display in connection warnings.
export const supabaseHost = (() => {
  try {
    return url ? new URL(url).host : '(nicht gesetzt)';
  } catch {
    return url ?? '(nicht gesetzt)';
  }
})();

// Hartes Zeitlimit für alle Anfragen des Supabase-Clients.
//
// Hintergrund (Stabilitäts-Vorfall): Der Client serialisiert alle Anfragen
// hinter einer Sitzungs-Sperre (Token-Refresh). Bleibt EIN Refresh-Request im
// Browser hängen (Netzwerk-Hänger, Tab war im Hintergrund), stauen sich ALLE
// folgenden Datenbank-Anfragen dahinter — jede Seite zeigt endlos „lädt…",
// bis man die Seite manuell neu lädt (das bricht den hängenden Request ab).
// Mit Zeitlimit bricht der hängende Request von selbst ab, die Sperre wird
// frei und die Retry-Logik in useCachedQuery lädt automatisch nach.
//
// Ausnahmen: Edge Functions (z. B. Kampagnen-Versand an viele Empfänger kann
// legitim lange dauern) und Storage. Von Aufrufern gesetzte AbortSignals
// (z. B. die Terminsuche) haben Vorrang und werden nicht überschrieben.
// Warum diese Werte: eine hängende Anfrage kostet den Nutzer das Zeitlimit MAL
// der Anzahl Wiederholungen (s. useCachedQuery). 20 s × 3 Versuche waren über
// eine Minute „Lade…" ohne jede Rückmeldung — genau die Beschwerde aus dem
// Telefon-Team. Kürzer ist hier besser: ein Neuversuch auf einer frischen
// Verbindung ist fast immer schneller als das Warten auf eine tote.
const AUTH_TIMEOUT_MS = 8_000;
const REST_TIMEOUT_MS = 12_000;

function timeoutFor(requestUrl: string): number | null {
  if (requestUrl.includes('/functions/')) return null;
  if (requestUrl.includes('/storage/')) return null;
  if (requestUrl.includes('/auth/v1/')) return AUTH_TIMEOUT_MS;
  return REST_TIMEOUT_MS;
}

const fetchWithTimeout: typeof fetch = (input, init) => {
  const requestUrl =
    typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
  const ms = timeoutFor(requestUrl);
  if (ms == null || init?.signal) return fetch(input, init);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return fetch(input, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer));
};

// Don't annotate as `SupabaseClient`: its default schema generic is "public",
// which mismatches our 'callcenter_telefonie' setting. Letting TS infer keeps
// the schema generic correct.
const client =
  !supabaseConfigError && url && key
    ? createClient(url, key, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
        db: { schema: 'callcenter_telefonie' },
        global: { fetch: fetchWithTimeout },
      })
    : null;

export const supabase = client!;
export const isSupabaseAvailable = client !== null;

export interface CrmPingResult {
  ok: boolean;          // reachable AND not rejected (status < 400)
  reachable: boolean;   // got any HTTP response at all
  status?: number;
  error?: string;
}

// Time-boxed reachability probe against the REST endpoint. Converts an
// otherwise-infinite hang (wrong/dead host) into a bounded yes/no so the UI
// can show a clear warning instead of an endless spinner.
//
// Subtlety: Supabase restricted the PostgREST root (`GET /rest/v1/`) to *secret*
// keys — a perfectly valid publishable/anon key now always gets back
// `401 "Secret API key required"` there. A naive `status < 400` check therefore
// flags every correctly configured deploy as "wrong key" (a false alarm that
// scares stakeholders). The API gateway, however, still validates the apikey
// *before* routing, so a key that doesn't belong to this project yields
// `401 "Invalid API key"` (or "No API key found") on every endpoint. We hang the
// "wrong key" verdict on that gateway signal, not on the raw status code.
export async function pingCrm(timeoutMs = 6000): Promise<CrmPingResult> {
  if (supabaseConfigError || !url || !key) {
    return { ok: false, reachable: false, error: supabaseConfigError ?? 'nicht konfiguriert' };
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${url.replace(/\/$/, '')}/rest/v1/`, {
      method: 'GET',
      headers: { apikey: key, Authorization: `Bearer ${key}` },
      signal: controller.signal,
    });
    if (res.status < 400) return { ok: true, reachable: true, status: res.status };
    // Reachable, but a 4xx/5xx came back. Only a gateway-level apikey rejection
    // means the configured key is genuinely wrong for this project; the root's
    // own "Secret API key required" (or a transient 5xx) is not a key mismatch.
    const body = await res.text().catch(() => '');
    const keyRejected =
      (res.status === 401 || res.status === 403) &&
      /invalid api key|no api key found/i.test(body);
    return {
      ok: !keyRejected,
      reachable: true,
      status: res.status,
      error: keyRejected ? 'apikey passt nicht zum Projekt' : undefined,
    };
  } catch (e) {
    const err = e as Error;
    return {
      ok: false,
      reachable: false,
      error: err.name === 'AbortError' ? `Zeitüberschreitung nach ${timeoutMs} ms` : err.message,
    };
  } finally {
    clearTimeout(timer);
  }
}

export function functionsUrl(name: string): string {
  const base = import.meta.env.VITE_FUNCTIONS_URL?.replace(/\/$/, '');
  if (base) return `${base}/${name}`;
  return `${url.replace('.supabase.co', '.functions.supabase.co')}/${name}`;
}

// Zeitlimit für Edge-Function-Aufrufe — mit echtem Abbruch.
//
// Vorfall 16.09.2026: Die Telefonie-Anmeldung gab nach 12 s auf, obwohl die
// Funktion noch antwortete; in der Störung kamen gültige Antworten nach 13–26 s
// und wurden weggeworfen. Schlimmer: der Aufruf hatte gar kein Abbruchsignal,
// die aufgegebene Anfrage lief im Hintergrund bis zu 30 s weiter und belegte
// dabei weiter einen Worker. Jeder erneute Klick legte eine weitere Anfrage
// obendrauf — die Störung hat sich dadurch selbst verstärkt.
//
// Das Budget liegt bewusst etwas ÜBER der Obergrenze der Edge-Runtime (die
// antwortet nach 30 s von sich aus mit 503): so gewinnt die echte Serverantwort
// mitsamt Statuscode, und unser Abbruch greift nur, wenn wirklich nichts kommt.
export const FUNCTION_TIMEOUT_MS = 32_000;

export async function callFunction<T = unknown>(
  name: string,
  body?: unknown,
  opts?: { timeoutMs?: number; signal?: AbortSignal }
): Promise<T> {
  // Uhr und Abbruchsignal stehen VOR dem ersten await. Sonst zählt das Budget
  // erst ab der Sitzungsabfrage — und die kann selbst hängen (sie läuft hinter
  // der Sitzungssperre, s. oben). Ein Aufrufer, der sofort abbricht, würde
  // andernfalls gar nicht gehört.
  const ms = opts?.timeoutMs ?? FUNCTION_TIMEOUT_MS;
  const controller = new AbortController();
  let abgelaufen = false;
  const timer = setTimeout(() => { abgelaufen = true; controller.abort(); }, ms);
  const durchreichen = () => controller.abort();
  if (opts?.signal?.aborted) controller.abort();
  else opts?.signal?.addEventListener('abort', durchreichen);

  try {
    const { data: session } = await supabase.auth.getSession();
    const token = session.session?.access_token;
    if (controller.signal.aborted) {
      throw Object.assign(new Error('Abgebrochen'), { name: 'AbortError' });
    }
    const res = await fetch(functionsUrl(name), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Edge function ${name} failed: ${res.status} ${text}`);
    }
    return (await res.json()) as T;
  } catch (err) {
    if (abgelaufen) {
      throw new Error(
        `Zeitüberschreitung nach ${Math.round(ms / 1000)} s — „${name}" hat nicht `
        + 'geantwortet. Bitte gleich noch einmal versuchen.'
      );
    }
    throw err;
  } finally {
    clearTimeout(timer);
    opts?.signal?.removeEventListener('abort', durchreichen);
  }
}
